import React, { ChangeEvent, Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Backdrop,
    CircularProgress,
    TextField,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Button,
    Grid2,
    TablePagination,
    Paper,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { RootState } from '../../redux/reducers';
import { importSites } from '../../redux/actions/siteActions';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { ClientDto, SiteDto, SiteType } from '../../api/ClientApi';
import AddIcon from '@mui/icons-material/Add';
import { SiteTypeSR } from '../../common/Types';
import SiteForm from './Form/SiteForm/SiteForm';
import { StyledTableCell } from '../../common/components/styled/StyledTable';
import { getColorForSiteType } from './SiteHelper';
import { DetailsContainerBox, DetailsTableContainerBox } from '../../common/ResponsiveStyle';
import {
    canManipulateSites,
    isEmployeeFinanceManager,
    isEmployeeProjectManager,
    isEmployeeWarehouseOwnerOnly,
} from '../../redux/selectors/employeeSelector';
import { useLocation, useParams } from 'react-router-dom';
import { getConstructionSites, getWarehouses } from '../../redux/selectors/sitesSelector';

import { MobileCardRow, mobileHiddenHeadSx, useIsMobile } from '../../common/components/mobile/MobilePrimitives';

type SiteListTableInfo = {
    siteId?: number;
    siteName?: string;
    siteType?: SiteType;
    address?: string;
    clientName?: string;
    projectName?: string;
};

function createData(site: SiteDto, client?: ClientDto): SiteListTableInfo {
    return {
        siteId: site.id,
        address: site.locationInfo?.address,
        clientName: site.project?.client?.shortName ?? site.project?.client?.name ?? client?.shortName ?? client?.name,
        projectName: site.project?.name,
        siteName: site.name,
        siteType: site.siteType,
    };
}

type SiteRowProps = {
    row: SiteListTableInfo;
    isAllowedToSeeSiteInfo: boolean;
    onSelect: (siteId?: number) => void;
};

/**
 * One site row: desktop table row or a mobile card.
 * Lives at module scope so React updates rows in place instead of remounting them on every parent render.
 */
function Row({ row, isAllowedToSeeSiteInfo, onSelect }: SiteRowProps) {
    const isMobile = useIsMobile();

    if (isMobile) {
        return (
            <MobileCardRow
                title={row.siteName ?? '/'}
                subtitle={row.address}
                header={
                    row.siteType ? (
                        <Chip
                            variant="outlined"
                            label={SiteTypeSR[row.siteType]}
                            color={getColorForSiteType(row.siteType)}
                            size="small"
                        />
                    ) : undefined
                }
                fields={[
                    { label: 'Klijent', value: row.clientName ?? '/' },
                    { label: 'Projekat', value: row.projectName ?? '/' },
                ]}
                onClick={isAllowedToSeeSiteInfo ? () => onSelect(row.siteId) : undefined}
            />
        );
    }

    return (
        <TableRow
            sx={{
                cursor: isAllowedToSeeSiteInfo ? 'pointer' : 'default',
                '&:hover': { bgcolor: isAllowedToSeeSiteInfo ? '#eee' : '#fff' },
            }}
            onClick={() =>
                isAllowedToSeeSiteInfo
                    ? onSelect(row.siteId)
                    : console.log('%cUSER IS NOT ALLOWED TO ACCESS THIS INFO', 'color: red')
            }
        >
            <TableCell component="th" scope="row">
                {row.siteName ?? '/'}
            </TableCell>
            <TableCell align="center">
                {row.siteType ? (
                    <Chip
                        variant="outlined"
                        label={SiteTypeSR[row.siteType]}
                        color={getColorForSiteType(row.siteType)}
                        size="small"
                    />
                ) : (
                    '/'
                )}
            </TableCell>
            <TableCell align="center">{row.address ? row.address : '/'}</TableCell>
            <TableCell align="center">{row.clientName ?? '/'}</TableCell>
            <TableCell align="center">{row.projectName ?? '/'}</TableCell>
            {/* <TableCell align="center">
                <IconButton
                    color="primary"
                    disabled={!row.siteId || isNaN(+row.siteId)}
                    onClick={() => onSelect(row.siteId)}
                >
                    <InfoOutlinedIcon />
                </IconButton>
            </TableCell> */}
        </TableRow>
    );
}

export default function SitesList(): ReactElement {
    const clients = useAppSelector((state: RootState) => state.clients.clients);

    const sites = useAppSelector((state: RootState) => state.sites.sites);
    const constructionSites = useAppSelector(getConstructionSites);
    const warehouses = useAppSelector(getWarehouses);

    const isHighestPrivilageWarehouseOwner = useAppSelector(isEmployeeWarehouseOwnerOnly);
    const isFinanceManager = useAppSelector(isEmployeeFinanceManager);
    const isProjectManager = useAppSelector(isEmployeeProjectManager);
    const canChangeSites = useAppSelector(canManipulateSites);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [openAddSiteForm, setOpenAddSiteForm] = useState<boolean>(false);
    const [filter, setFilter] = useState<string>('');

    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const location = useLocation();
    const path = location.pathname;

    const params = useParams();

    const isConstSite = path.indexOf('/constructionSites') !== -1;
    const isWarehouse = path.indexOf('/warehouses') !== -1;
    const isProject = path.indexOf('/projects') !== -1;

    const currSites = useMemo(() => {
        if (isProject) {
            const projId = params.projectId;

            if (projId !== undefined && !isNaN(+projId) && +projId > 0) {
                return sites?.filter((s) => s.projectId === +projId);
            }
        } else {
            return isConstSite ? constructionSites : isWarehouse ? warehouses : sites;
        }

        return [];
    }, [path, sites]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        if (!sites) {
            dispatch(importSites());
        }
    }, [dispatch]);

    const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFilter(value);
    };

    const handleOpenSiteForm = () => {
        setOpenAddSiteForm(true);
    };

    const handleCloseSiteForm = () => {
        setOpenAddSiteForm(false);
    };

    const filteredSites = currSites?.filter((s) => {
        const client = clients?.find((c) => c.id === s.project?.clientId);

        return (
            s.name?.toLowerCase().includes(filter.toLowerCase()) ||
            s.locationInfo?.address.toLowerCase().includes(filter.toLowerCase()) ||
            client?.shortName?.toLowerCase().includes(filter.toLowerCase()) ||
            client?.name?.toLowerCase().includes(filter.toLowerCase()) ||
            s.project?.name?.toLowerCase().includes(filter.toLowerCase())
        );
    });

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (filteredSites?.length ?? 0)) : 0;

    const visibleSites = React.useMemo(
        () => (!filteredSites ? [] : [...filteredSites].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
        [page, rowsPerPage, clients, sites, filter, path],
    );

    const goToSite = (siteId?: number) => {
        if (!!siteId) {
            if (isProject) {
                navigate('/constructionSites/' + siteId.toString());
            } else {
                navigate(siteId.toString());
            }
        } else {
            alert('Ne postoje detalji o skladištu');
        }
    };

    React.useEffect(() => {
        if (!sites || sites?.length <= page * rowsPerPage) {
            setPage(0);
        }
    }, [sites, page]);

    const isAllowedToSeeSiteInfo = (isProject && (isProjectManager || isFinanceManager)) || !isProject;

    return sites === null ? (
        <Fragment>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={!Boolean(sites)}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Fragment>
    ) : (
        <>
            <DetailsContainerBox
                sx={{
                    p: 2,
                    backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'white' : theme.palette.grey[900]),
                }}
            >
                <Grid2 container sx={{ py: 2, px: { xs: 2, sm: 4 }, justifyContent: 'space-between', gap: 2 }}>
                    <Grid2>
                        <Typography variant="h5">
                            {isConstSite ? 'Gradilišta' : isWarehouse ? 'Magacini' : 'Skladišta'}
                        </Typography>
                    </Grid2>
                    {!isHighestPrivilageWarehouseOwner && canChangeSites && (
                        <Grid2>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={handleOpenSiteForm}
                                startIcon={<AddIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                {isConstSite ? 'Novo gradilište' : isWarehouse ? 'Novi magacin' : 'Novo skladište'}
                            </Button>
                        </Grid2>
                    )}
                </Grid2>
                <hr />
                <Box sx={{ p: { xs: 1, sm: 4 }, pt: 2 }}>
                    <TextField
                        size="small"
                        sx={{ mb: 1, width: '100%' }}
                        placeholder="Filter"
                        value={filter}
                        onChange={handleFilterChange}
                    />
                    <Box sx={{ p: 0, mt: 2 }}></Box>

                    <Box sx={{ width: '100%' }}>
                        <Paper sx={{ width: '100%', mb: 2 }}>
                            <DetailsTableContainerBox>
                                <Table size="small" aria-label="purchases">
                                    <TableHead sx={mobileHiddenHeadSx}>
                                        <TableRow>
                                            <StyledTableCell>Naziv</StyledTableCell>
                                            <StyledTableCell align="center">Tip</StyledTableCell>
                                            <StyledTableCell align="center">Lokacija</StyledTableCell>
                                            <StyledTableCell align="center">Klijent</StyledTableCell>
                                            <StyledTableCell align="center">Projekat</StyledTableCell>
                                            {/* <StyledTableCell align="center">Akcija </StyledTableCell> */}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {visibleSites.length === 0 && (
                                            <TableRow sx={{ height: 500 }}>
                                                <TableCell rowSpan={3} colSpan={5}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: { xs: 28, sm: 50 },
                                                                width: { xs: '100%', sm: 400 },
                                                                margin: 'auto',
                                                                color: '#aaa',
                                                            }}
                                                        >
                                                            Nema gradilišta
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {visibleSites?.map((site) => {
                                            const client = clients?.find((c) => c.id === site.project?.clientId);
                                            const newRowInfo: SiteListTableInfo = createData(site, client);

                                            return (
                                                <Row
                                                    key={site.id}
                                                    row={newRowInfo}
                                                    isAllowedToSeeSiteInfo={isAllowedToSeeSiteInfo}
                                                    onSelect={goToSite}
                                                />
                                            );
                                        })}
                                        {emptyRows > 0 && (
                                            <TableRow
                                                style={{
                                                    height: 40 * emptyRows,
                                                }}
                                            >
                                                <TableCell colSpan={6} />
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </DetailsTableContainerBox>
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50]}
                                component="div"
                                count={filteredSites?.length ?? 0}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>
                    </Box>
                </Box>
            </DetailsContainerBox>
            {!isHighestPrivilageWarehouseOwner && <SiteForm open={openAddSiteForm} closeForm={handleCloseSiteForm} />}
        </>
    );
}
